import type { EncounterService } from '@quromedical/fhir-common';

import { type AssertRule, compose, resolveConstant } from './composer';

type PathologyParams = {
  serviceType?: EncounterService;
  hasMedicalAid: boolean;
  hasMedicalAidAuthorization: boolean;
};

type RuleName = EncounterService | 'no-service-type';

type AssertEncounterServiceRule = AssertRule<PathologyParams>;

type EncounterServiceRules = Partial<
  Record<RuleName, AssertEncounterServiceRule[]>
>;

const assertHasServiceType: AssertEncounterServiceRule = ({ serviceType }) => {
  if (serviceType) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['Service type is required'],
  };
};

const assertHasMedicalAid: AssertEncounterServiceRule = ({ hasMedicalAid }) => {
  if (hasMedicalAid) {
    return undefined;
  }
  return {
    type: 'error',
    messages: ['Patient does not have medical aid'],
  };
};

const assertHasMedicalAidAuthorization: AssertEncounterServiceRule = ({
  hasMedicalAidAuthorization,
}) => {
  if (hasMedicalAidAuthorization) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['Patient does not have medical aid authorization'],
  };
};

const defaultCanRequestPathologyRules: AssertEncounterServiceRule[] = [
  assertHasServiceType,
  assertHasMedicalAid,
  assertHasMedicalAidAuthorization,
];

const encounterServiceRules: EncounterServiceRules = {
  'encounter.service.tcp': [assertHasMedicalAid],
};

/**
 * a function that takes in a set of parameters and based on that determines if a consumer can do a pathology request for the patient
 */
export const validatePathologyRequest = compose({
  getRuleName: ({ serviceType }) => serviceType || 'no-service-type',
  assertions: encounterServiceRules,
  defaultAssertions: defaultCanRequestPathologyRules,
  defaultResolver: resolveConstant<true>(true),
});
